/* Printers.
 *
 * Not a register of its own. A printer already has an asset tag, a serial, a
 * warranty and a depot, and the NinjaOne sync fills those in — so a second
 * record for the same machine would be two things to keep in step, and one of
 * them always loses. This is the Devices register with everything that is not a
 * printer filtered out, and the columns a printer is actually looked up for.
 *
 * Which is worth saying plainly, because nobody opens a printer record to read
 * its serial number. They open it because it has run out of something and they
 * need the cartridge code before ringing the supplier, or because somebody says
 * "the printer by accounts is jammed" and there are four of them.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDepotScope, scoped } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { daysSince, fmtDate } from '../../lib/format';
import { useOpenRecord } from '../../hooks/useOpenRecord';
import type { IDevice } from '../../models';
import { qk } from '../../query/keys';
import { BulkBar, useBulk } from './BulkBar';
import { useServices } from '../../services/context';
import { DEVICE_SEEN, DEVICE_STATUS, deviceFields, valuesSeen } from '../../lib/registerFields';
import { PRINTER_TYPES } from '../../lib/deviceGroups';
import { DeviceDetail } from './DeviceDetail';
import { EditDrawer } from './EditDrawer';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  LoadError,
  Panel,
  SearchBox,
  SummaryTile,
  SummaryTiles,
  TableSkeleton,
  type Column,
} from '../ui';
import { Copyable } from './Copyable';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/** A meter reading older than this is a number rather than a measurement — a
    quarter is about how often anybody is asked for one. */
const METER_STALE_DAYS = 120;

export function PrintersScreen() {
  const svc = useServices();
  const { scope } = useDepotScope();
  const depots = useDepots();

  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<IDevice | null>(null);

  /* Arriving from a link elsewhere — the network screen's address panel, a
     backup job, the global search. */
  useOpenRecord(
    devices.data ?? [],
    (d, value) => d.id === value || d.tag.toLowerCase() === value.toLowerCase(),
    setSelected,
  );

  const qc = useQueryClient();

  /* Adding from here rather than sending somebody to Devices to remember to set
     the type. The record is a device either way — this screen just knows which
     kind, so the form arrives already knowing it and shows the printer fields
     from the first keystroke instead of after the type is chosen. */
  /* The same three columns as Devices, because this is the same register seen
     through a filter and the bar should not behave differently depending on
     which list you reached it from. All three are Ledger's own, so a bulk
     write stands rather than being recomputed overnight. */
  const bulk = useBulk({
    rows: devices.data ?? [],
    idOf: (d) => d.id,
    nameOf: (d) => d.tag,
    fields: {
      loc: {
        label: 'Depot',
        options: depots.map((x) => ({ value: x.id, label: x.name })),
        note: 'Depot is yours — no sync has an opinion about where a machine lives.',
      },
      status: {
        label: 'Status',
        options: DEVICE_STATUS.map((x) => ({ value: x, label: x })),
        note: 'Status is yours and stands. Compliance and last seen come from Intune and are not offered here.',
      },
      ownership: {
        label: 'Owned or hired',
        options: [
          { value: 'Purchased', label: 'Purchased' },
          { value: 'Hired', label: 'Hired' },
        ],
        note: 'Yours. Changing it to Hired swaps the purchase and warranty dates for the hire contract on each record.',
      },
    },
    update: (id, patch) => svc.devices.update(id, patch as Partial<IDevice>),
    remove: (id) => svc.devices.remove(id),
    invalidate: [qk.devices, qk.audit],
  });

  const create = useMutation({

    meta: { ok: 'Printer added' },
    mutationFn: (values: Record<string, unknown>) =>
      svc.devices.create({ ...(values as Omit<IDevice, 'id'>), type: 'Printer' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setAdding(false);
    },
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scoped(devices.data ?? [], scope)
      .filter((d) => d.type === 'Printer')
      .filter(
        (d) =>
          !q ||
          [d.tag, d.make, d.model, d.placement, d.consumables, d.printQueue, d.ip]
            .some((v) => (v ?? '').toLowerCase().includes(q)),
      )
      .sort((a, b) => a.tag.localeCompare(b.tag));
  }, [devices.data, scope, query]);

  /* The two things that make a printer record useless the moment somebody needs
     it: no cartridge code, and no idea where it is. */
  const gaps = useMemo(
    () => ({
      noConsumables: rows.filter((d) => !d.consumables?.trim()).length,
      noPlacement: rows.filter((d) => !d.placement?.trim()).length,
    }),
    [rows],
  );

  const columns: Column<IDevice>[] = [
    {
      key: 'tag',
      header: 'Printer',
      sortBy: (d) => d.tag,
      /* The whole row opens it, as on Devices. A link on the tag alone means
         the other nine tenths of the row does nothing when clicked, which is
         not what a row that highlights on hover promises. */
      render: (d) => (
        <>
          <div className={s.cellMain}>{d.tag}</div>
          <div className={s.cellSub}>
            {d.make} {d.model}
          </div>
        </>
      ),
    },
    {
      key: 'placement',
      header: 'Where',
      sortBy: (d) => d.placement ?? '',
      render: (d) =>
        d.placement ? (
          <>
            <div>{d.placement}</div>
            <div className={s.cellSub}>{depots.find((x) => x.id === d.loc)?.name ?? d.loc}</div>
          </>
        ) : (
          <span className={s.cellSub}>{depots.find((x) => x.id === d.loc)?.name ?? d.loc}</span>
        ),
    },
    {
      key: 'consumables',
      header: 'Cartridges',
      sortBy: (d) => d.consumables ?? '',
      /* The column this screen exists for. Blank is the finding, not a gap in
         the table — a printer record with no cartridge code answers none of the
         questions anybody asks it. */
      render: (d) =>
        d.consumables ? (
          <span className={s.mono}>{d.consumables}</span>
        ) : (
          <Badge tone="warn">Not recorded</Badge>
        ),
    },
    {
      key: 'queue',
      priority: 3,
      header: 'Queue and address',
      render: (d) => (
        <>
          {d.printQueue ? <div>{d.printQueue}</div> : <span className={s.cellSub}>No queue recorded</span>}
          {d.ip && <Copyable value={d.ip} />}
        </>
      ),
    },
    {
      key: 'meter',
      priority: 2,
      header: 'Meter',
      sortBy: (d) => d.meterReading ?? -1,
      render: (d) => {
        if (d.meterReading === undefined) return <span className={s.cellSub}>—</span>;
        const stale = d.meterDate ? daysSince(d.meterDate) > METER_STALE_DAYS : true;
        return (
          <>
            <div className={s.mono}>{d.meterReading.toLocaleString()}</div>
            <div className={s.cellSub}>
              {d.meterDate ? fmtDate(d.meterDate) : 'undated'}
              {stale && ' · stale'}
            </div>
          </>
        );
      },
    },
    {
      key: 'web',
      header: '',
      width: '60px',
      render: (d) =>
        d.webUI ? (
          <a href={d.webUI} target="_blank" rel="noreferrer" title="Open its web interface">
            <i className="ti ti-external-link" aria-hidden />
          </a>
        ) : null,
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Printers</h1>
          <p>
            Printers, with the cartridge codes, where each one is, and how to reach it.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => setAdding(true)}>
          Add printer
        </Button>
      </div>

      {devices.isError && <LoadError error={devices.error} what="devices" />}

      <SummaryTiles>
        <SummaryTile icon="ti-printer" value={String(rows.length)} label="Printers" />
        <SummaryTile
          icon="ti-droplet-off"
          value={String(gaps.noConsumables)}
          label="No cartridge recorded"
          tone={gaps.noConsumables > 0 ? 'warn' : 'ok'}
        />
        <SummaryTile
          icon="ti-map-pin-off"
          value={String(gaps.noPlacement)}
          label="No location recorded"
          tone={gaps.noPlacement > 0 ? 'warn' : 'ok'}
        />
      </SummaryTiles>

      <div className={s.stack}>
        <Controls count={`${rows.length} shown`}>
          <SearchBox value={query} onChange={setQuery} placeholder="Tag, model, location or code…" />
        </Controls>

      <BulkBar
        bulk={bulk}
        onDelete={() => {
          const n = bulk.ticked.size;
          return window.confirm(
            `Remove ${n} ${n === 1 ? 'printer' : 'printers'} from ${PRODUCT.name}?` +
              '\n\nThis deletes nothing in Intune or NinjaOne — it only removes the record here.' +
              '\n\nAnything still managed will be recreated at the next sync, without the purchase date, warranty or notes.',
          );
        }}
      />


        <Panel flush>
          {devices.isLoading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(d) => d.id}
              checked={bulk.ticked}
              onCheckedChange={bulk.setTicked}
              onRowClick={setSelected}
              selectedKey={selected?.id}
              initialSort={{ key: 'tag', dir: 'asc' }}
              empty={
                <EmptyState icon="ti-printer" title="No printers recorded">
                  A printer is a device with its type set to Printer. Set that on the Devices
                  screen and it appears here, with somewhere to put the cartridge codes.
                </EmptyState>
              }
            />
          )}
        </Panel>
      </div>


      {/* The record opens here rather than throwing you onto Devices. One
          machine, one panel — it just does not belong to one of the lists. */}
      <DeviceDetail device={selected} onClose={() => setSelected(null)} />

      <EditDrawer
        open={adding}
        record={null}
        /* Already a printer, so the form shows cartridges and location straight
           away rather than waiting for the type to be picked. */
        initial={{ type: 'Printer' }}
        fields={deviceFields(depots, PRINTER_TYPES, valuesSeen(devices.data, DEVICE_SEEN))}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="printer"
        icon="ti-printer"
        onClose={() => setAdding(false)}
        onSave={(values) => create.mutate(values)}
        saving={create.isPending}
        error={create.error}
      />
    </>
  );
}
